import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useMediaQuery } from '@/hooks/useResponsive';

import styles from './index.module.less';

// 定义章节的数据结构
export interface Section {
  id: string;
  title: string;
  content: ReactNode;
}

// 页面布局组件的属性定义
interface PageWithTOCProps {
  title: string;
  subtitle?: string;
  sections: Section[];
  headerSlot?: ReactNode;
  footerSlot?: ReactNode;
}

// 右侧目录组件
const TableOfContents: React.FC<{
  sections: Section[];
  activeSection: string;
  onSectionClick: (sectionId: string) => void;
}> = ({ sections, activeSection, onSectionClick }) => (
  <div className="min-h-[500px] w-full border-l border-[#484F59] pl-[29px]">
    <div className="flex flex-col">
      <nav>
        <ul className="flex flex-col gap-[12px]">
          {sections
            .filter((section) => !!section.title)
            .map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => onSectionClick(section.id)}
                  className={`w-full cursor-pointer text-left text-sm transition-colors ${activeSection === section.id ? 'font-semibold text-content-primary' : 'text-[#B4B4B4]'
                    }`}
                >
                  {section.title}
                </button>
              </li>
            ))}
        </ul>
      </nav>
    </div>
  </div>
);

// 主布局组件
const PageWithTOC: React.FC<PageWithTOCProps> = ({ title, subtitle, sections }) => {
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const containerRef = useRef<HTMLDivElement>(null); // 添加容器引用
  const [activeSection, setActiveSection] = useState(sections[0]?.id || '');

  // 检测屏幕宽度，当宽度小于1700px时隐藏右侧菜单
  // const shouldShowTOC = useMediaQuery('(min-width: 1700px)');
  const shouldShowTOC = false;

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const targetElement = sectionRefs.current[sectionId];
    if (targetElement) {
      // 在移动端或没有TOC时，使用页面滚动
      if (!shouldShowTOC) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        // 在桌面端有TOC时，使用容器内滚动
        const container = containerRef.current;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const targetRect = targetElement.getBoundingClientRect();
          const relativeTop = targetRect.top - containerRect.top;

          container.scrollBy({
            top: relativeTop - containerRect.height / 2 + targetRect.height / 2,
            behavior: 'smooth',
          });
        }
      }
    }
  };

  // 添加滚动监听
  useEffect(() => {
    if (!shouldShowTOC) return; // 只在显示TOC时添加滚动监听

    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const containerRect = container.getBoundingClientRect();
      const sectionPositions = sections.map((section) => {
        const element = sectionRefs.current[section.id];
        if (!element) return { id: section.id, top: 0 };
        const rect = element.getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;
        return {
          id: section.id,
          top: relativeTop,
        };
      });

      const activeSection = sectionPositions.reduce((closest, current) => {
        if (
          Math.abs(current.top - containerRect.height / 2) <
          Math.abs(closest.top - containerRect.height / 2)
        ) {
          return current;
        }
        return closest;
      });

      setActiveSection(activeSection.id);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [sections, shouldShowTOC]);

  return (
    <div
      className={`relative z-[1] flex gap-[80px] px-4 pt-[166px] md:gap-[80px] md:px-[120px] ${shouldShowTOC ? 'md:h-[calc(100vh-82px)] md:overflow-hidden' : ''}`}
    >
      {/* 左侧内容区域 */}
      <div
        className={`${styles.container} flex-auto text-content-primary ${shouldShowTOC ? 'md:h-full md:pr-[345px]' : ''}`}
      >
        <h1 className="text-2xl font-semibold leading-[32px] text-content-primary md:text-[34px]">{title}</h1>
        <div className="my-6 h-px w-full bg-[#484F59] md:my-[36px]" />
        {subtitle && <div className="text-base font-normal text-content-primary">{subtitle}</div>}

        <div
          className={`scrollbar-hide mt-8 pb-8 md:mt-[50px] ${shouldShowTOC ? 'md:h-[calc(100%-210px)] md:overflow-y-auto md:pb-0' : 'md:pb-8'}`}
          ref={containerRef}
        >
          {sections.map((section) => (
            <div
              key={section.id}
              ref={(el) => (sectionRefs.current[section.id] = el)}
              className="mb-8 md:mb-[60px]"
            >
              <h2 className="mb-4 text-lg font-semibold leading-[32px] md:mb-[24px] md:text-[22px]">
                {section.title}
              </h2>
              {section.content}
            </div>
          ))}
        </div>
      </div>

      {/* 右侧导航区域 - 根据屏幕宽度动态显示 */}
      {shouldShowTOC && (
        <div className="fixed right-[80px] top-[124px] hidden w-[350px] shrink-0 grow-0 pt-[200px] md:flex">
          <TableOfContents
            sections={sections}
            activeSection={activeSection}
            onSectionClick={scrollToSection}
          />
        </div>
      )}
    </div>
  );
};

export default PageWithTOC;
